import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavReset } from '../../components/nav-reset.js';
import { useNotice } from '../../components/notice.js';
import { Badge, Button, Callout, Card, Loading, Modal, Mono, Screen } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { NewCustomerDialog } from '../customers/new-customer.js';
import { useRepairPrinter } from './repair-documents.js';
import type { RepairDetail } from './repair-documents.js';
import {
  SIGNATURE_HEIGHT,
  SIGNATURE_WIDTH,
  SignatureImage,
  SignaturePad,
} from './signature-pad.js';
import './repairs.css';
import { DateInput } from '../../components/date-input.js';

/**
 * Repairs (SCOPE §11.1, decision 127) — the fall tune-up season's front door.
 *
 * Three jobs, three views:
 *
 *  - **The board** is what a technician looks at all day: every open ticket,
 *    in three columns — taken in, in progress, ready for pickup — with the
 *    promise date said in words ("Due tomorrow", "Overdue by 2 days") and the
 *    next seven days' load along the top, so a full Friday is visible on
 *    Monday. The *Ready for pickup* column is the pickup list.
 *  - **Intake** is at the counter with the customer standing there: who,
 *    what, which services (priced from the service catalog — the quote), when
 *    it is promised, and the waiver, signed on the screen. Nothing is taken in
 *    unsigned.
 *  - **The ticket** moves the work along, prints the claim ticket and the
 *    signed waiver, and at pickup rings it up at the register — pickup is a
 *    sale, so the money is in end of day like any other.
 */

type Mode =
  { kind: 'board' } | { kind: 'intake' } | { kind: 'ticket'; id: string; fresh?: boolean };
type Status = 'intake' | 'in_progress' | 'ready' | 'picked_up' | 'cancelled';

const COLUMNS: { status: 'intake' | 'in_progress' | 'ready'; title: string }[] = [
  { status: 'intake', title: 'Taken in' },
  { status: 'in_progress', title: 'In progress' },
  { status: 'ready', title: 'Ready for pickup' },
];

const STATUS_WORDS: Record<Status, string> = {
  intake: 'taken in',
  in_progress: 'in progress',
  ready: 'ready for pickup',
  picked_up: 'picked up',
  cancelled: 'canceled',
};

const STATUS_TONE: Record<Status, 'info' | 'warning' | 'success' | 'neutral'> = {
  intake: 'neutral',
  in_progress: 'info',
  ready: 'success',
  picked_up: 'neutral',
  cancelled: 'neutral',
};

/** Local calendar day as YYYY-MM-DD — a promise is a day in the shop, not a UTC instant. */
function localDay(offsetDays = 0): string {
  const day = new Date();
  day.setDate(day.getDate() + offsetDays);
  const month = String(day.getMonth() + 1).padStart(2, '0');
  return `${day.getFullYear()}-${month}-${String(day.getDate()).padStart(2, '0')}`;
}

function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T12:00:00`).getTime() - new Date(`${from}T12:00:00`).getTime()) / 86_400_000,
  );
}

/** The promise, in words — and whether it has been missed. */
function dueWords(promised: string | null, status: Status): { text: string; late: boolean } {
  if (promised === null) return { text: 'No date promised', late: false };
  const days = daysBetween(localDay(), promised);
  if (status === 'ready' || status === 'picked_up') {
    return { text: `Promised ${shortDate(promised)}`, late: false };
  }
  if (days < 0) {
    return { text: `Overdue by ${-days} day${days === -1 ? '' : 's'}`, late: true };
  }
  if (days === 0) return { text: 'Due today', late: false };
  if (days === 1) return { text: 'Due tomorrow', late: false };
  return { text: `Due ${shortDate(promised)}`, late: false };
}

function shortDate(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function RepairsScreen({ onRungUp }: { onRungUp: () => void }) {
  const [mode, setMode] = useState<Mode>({ kind: 'board' });
  const queryClient = useQueryClient();

  const toBoard = () => {
    void queryClient.invalidateQueries({ queryKey: ['repairs', 'board'] });
    setMode({ kind: 'board' });
  };
  // Pressing Repairs while here goes back to the board (decision 110).
  useNavReset(toBoard);

  if (mode.kind === 'intake') {
    return (
      <IntakeScreen
        onCancel={toBoard}
        onTakenIn={(id) => setMode({ kind: 'ticket', id, fresh: true })}
      />
    );
  }
  if (mode.kind === 'ticket') {
    return (
      <TicketScreen
        ticketId={mode.id}
        fresh={mode.fresh === true}
        onBack={toBoard}
        onRungUp={onRungUp}
      />
    );
  }
  return (
    <Board
      onOpen={(id) => setMode({ kind: 'ticket', id })}
      onIntake={() => setMode({ kind: 'intake' })}
    />
  );
}

// --- the board ----------------------------------------------------------------

function Board({ onOpen, onIntake }: { onOpen: (id: string) => void; onIntake: () => void }) {
  const [technician, setTechnician] = useState('');
  const board = useQuery({
    queryKey: ['repairs', 'board'],
    queryFn: () => api.repairs.board.query(),
    /*
     * A live queue re-asks every time it is opened. With the app's usual
     * fifteen seconds of freshness, skis picked up at the register a moment
     * ago were still shown as waiting (found by the pickup journey).
     */
    refetchOnMount: 'always',
    refetchInterval: 30_000,
  });
  const roster = useQuery({
    queryKey: ['auth', 'pinPadRoster'],
    queryFn: () => api.auth.pinPadRoster.query(),
    staleTime: 60_000,
  });

  const tickets = (board.data ?? []).filter(
    (ticket) => technician === '' || ticket.technicianId === technician,
  );

  /*
   * Capacity at a glance: what is promised on each of the next seven days,
   * and what has already been missed. Ready tickets are done work and do not
   * count against a day.
   */
  const load = useMemo(() => {
    const working = tickets.filter((ticket) => ticket.status !== 'ready');
    const today = localDay();
    const overdue = working.filter(
      (ticket) => ticket.promisedDate !== null && ticket.promisedDate < today,
    ).length;
    const days = Array.from({ length: 7 }, (_, offset) => {
      const day = localDay(offset);
      return {
        day,
        label: offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : shortDate(day),
        count: working.filter((ticket) => ticket.promisedDate === day).length,
      };
    });
    return { overdue, days };
  }, [tickets]);

  return (
    <Screen
      title="Repairs"
      subtitle="Tune-ups and repairs in the shop, and when each one is promised."
      width="wide"
      primaryAction={
        <Button variant="primary" onClick={onIntake}>
          Take in a repair
        </Button>
      }
    >
      <div className="repairs__toolbar">
        <label className="repairs__filter">
          <span className="label-micro">Technician</span>
          <select
            className="select"
            value={technician}
            onChange={(event) => setTechnician(event.target.value)}
          >
            <option value="">Everyone</option>
            {(roster.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.displayName}
              </option>
            ))}
          </select>
        </label>
        <ol className="repairs__load" aria-label="Work promised over the next seven days">
          <li className={`repairs__load-day${load.overdue > 0 ? ' repairs__load-day--late' : ''}`}>
            <span className="label-micro">Overdue</span>
            <span className="repairs__load-count">{load.overdue}</span>
          </li>
          {load.days.map((day) => (
            <li key={day.day} className="repairs__load-day">
              <span className="label-micro">{day.label}</span>
              <span className="repairs__load-count">{day.count}</span>
            </li>
          ))}
        </ol>
      </div>

      {board.isError ? <Callout tone="danger">{errorMessage(board.error)}</Callout> : null}
      {board.isPending ? <Loading label="Loading the board" /> : null}

      {board.data !== undefined && board.data.length === 0 ? (
        <Card>
          <p className="text-secondary">
            Nothing is in the shop. When skis or a board come in, press <em>Take in a repair</em> —
            the customer signs the waiver on this screen.
          </p>
        </Card>
      ) : null}

      {board.data !== undefined && board.data.length > 0 ? (
        <div className="repairs__board">
          {COLUMNS.map((column) => {
            const inColumn = tickets.filter((ticket) => ticket.status === column.status);
            return (
              <section
                key={column.status}
                className="repairs__column"
                aria-label={`${column.title}: ${inColumn.length}`}
              >
                <h2 className="repairs__column-title">
                  {column.title} <span className="repairs__column-count">{inColumn.length}</span>
                </h2>
                {inColumn.length === 0 ? (
                  <p className="text-secondary repairs__column-empty">None.</p>
                ) : (
                  inColumn.map((ticket) => {
                    const due = dueWords(ticket.promisedDate, ticket.status);
                    return (
                      <button
                        key={ticket.id}
                        type="button"
                        className="repair-card"
                        onClick={() => onOpen(ticket.id)}
                      >
                        <span className="repair-card__top">
                          <Mono>{ticket.number}</Mono>
                          {due.late ? <Badge tone="warning">{due.text}</Badge> : null}
                        </span>
                        <span className="repair-card__customer">{ticket.customerName}</span>
                        <span className="repair-card__equipment">{ticket.equipment}</span>
                        <span className="repair-card__meta">
                          {due.late ? null : <span>{due.text}</span>}
                          <span>{ticket.technicianName ?? 'Not assigned'}</span>
                        </span>
                      </button>
                    );
                  })
                )}
              </section>
            );
          })}
        </div>
      ) : null}
    </Screen>
  );
}

// --- intake ---------------------------------------------------------------------

function IntakeScreen({
  onCancel,
  onTakenIn,
}: {
  onCancel: () => void;
  onTakenIn: (ticketId: string) => void;
}) {
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [equipment, setEquipment] = useState('');
  const [lines, setLines] = useState<{ variantId: string; quantity: number }[]>([]);
  // Smart default (§2.2-5): most tune-ups are promised a few days out.
  const [promisedDate, setPromisedDate] = useState(localDay(3));
  const [technicianId, setTechnicianId] = useState('');
  const [notes, setNotes] = useState('');
  const [signedName, setSignedName] = useState('');
  const [signature, setSignature] = useState('');

  const services = useQuery({
    queryKey: ['repairs', 'services'],
    queryFn: () => api.repairs.services.query(),
  });
  const roster = useQuery({
    queryKey: ['auth', 'pinPadRoster'],
    queryFn: () => api.auth.pinPadRoster.query(),
    staleTime: 60_000,
  });
  const waiver = useQuery({
    queryKey: ['repairs', 'waiver', customer?.id],
    queryFn: () => api.repairs.waiver.query({ customerId: customer!.id }),
    enabled: customer !== null,
  });

  // The signer is usually the customer; their name is the smart default.
  useEffect(() => {
    if (customer !== null) setSignedName(customer.name);
  }, [customer]);

  const byId = new Map((services.data ?? []).map((service) => [service.variantId, service]));
  const quote = lines.reduce(
    (sum, line) => sum + line.quantity * (byId.get(line.variantId)?.retailCents ?? 0),
    0,
  );
  const needsSignature = waiver.data !== undefined && waiver.data.onFile === null;

  const create = useMutation({
    mutationFn: () =>
      api.repairs.create.mutate({
        customerId: customer!.id,
        equipmentDescription: equipment.trim(),
        notes: notes.trim() === '' ? null : notes.trim(),
        promisedDate: promisedDate === '' ? null : promisedDate,
        technicianId: technicianId === '' ? null : technicianId,
        lines,
        waiver: needsSignature
          ? {
              signedName: signedName.trim(),
              signaturePath: signature,
              width: SIGNATURE_WIDTH,
              height: SIGNATURE_HEIGHT,
            }
          : null,
      }),
    onSuccess: (created) => {
      recordBreadcrumb('repairs', `took in ${created.number}`);
      onTakenIn(created.id);
    },
  });

  const blocked =
    customer === null
      ? 'Find or add the customer'
      : equipment.trim() === ''
        ? 'Say what the equipment is'
        : lines.length === 0
          ? 'Add at least one service'
          : waiver.data === undefined
            ? 'Checking for a waiver on file…'
            : needsSignature && signature === ''
              ? 'The customer signs the waiver first'
              : needsSignature && signedName.trim() === ''
                ? 'Type the name of the person signing'
                : undefined;

  const add = (variantId: string) =>
    setLines((current) =>
      current.some((line) => line.variantId === variantId)
        ? current.map((line) =>
            line.variantId === variantId ? { ...line, quantity: line.quantity + 1 } : line,
          )
        : [...current, { variantId, quantity: 1 }],
    );

  return (
    <Screen
      title="Take in a repair"
      subtitle="Who, what, which services and when — and the waiver, signed before the work is taken in."
      width="wide"
      primaryAction={
        <Button
          variant="primary"
          busy={create.isPending}
          disabledReason={blocked}
          onClick={() => create.mutate()}
        >
          Take it in
        </Button>
      }
      secondaryActions={
        <Button variant="ghost" onClick={onCancel}>
          Back to the board
        </Button>
      }
    >
      {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}

      <div className="intake">
        <Card title="Customer">
          <CustomerChooser customer={customer} onChosen={setCustomer} />
        </Card>

        <Card title="The work">
          <div className="field">
            <label className="field__label" htmlFor="intake-equipment">
              Equipment
            </label>
            <input
              id="intake-equipment"
              className="input"
              maxLength={300}
              placeholder="Rossignol Experience 88, 176 cm, Look bindings"
              value={equipment}
              onChange={(event) => setEquipment(event.target.value)}
            />
          </div>

          <div className="field">
            <span className="field__label">Services</span>
            {services.data !== undefined && services.data.length === 0 ? (
              <Callout tone="warning">
                There are no services in the catalog yet. Add one in Inventory as a model marked as
                a service — a tune, a wax, a mount — with its price.
              </Callout>
            ) : (
              <div className="chips">
                {(services.data ?? []).map((service) => (
                  <button
                    key={service.variantId}
                    type="button"
                    className="chip"
                    onClick={() => add(service.variantId)}
                  >
                    {service.name}
                    {service.sizeLabel ? ` · ${service.sizeLabel}` : ''} ·{' '}
                    {money(service.retailCents)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {lines.length > 0 ? (
            <table className="table intake__lines">
              <tbody>
                {lines.map((line) => {
                  const service = byId.get(line.variantId);
                  return (
                    <tr key={line.variantId}>
                      <td>{service?.name ?? 'Service'}</td>
                      <td className="num">
                        <span className="intake__qty">
                          <Button
                            variant="ghost"
                            aria-label={`One fewer ${service?.name ?? ''}`}
                            onClick={() =>
                              setLines((current) =>
                                current
                                  .map((entry) =>
                                    entry.variantId === line.variantId
                                      ? { ...entry, quantity: entry.quantity - 1 }
                                      : entry,
                                  )
                                  .filter((entry) => entry.quantity > 0),
                              )
                            }
                          >
                            −
                          </Button>
                          <span>{line.quantity}</span>
                          <Button
                            variant="ghost"
                            aria-label={`One more ${service?.name ?? ''}`}
                            onClick={() => add(line.variantId)}
                          >
                            +
                          </Button>
                        </span>
                      </td>
                      <td className="num money">
                        {money(line.quantity * (service?.retailCents ?? 0))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={2}>Quote, before tax</th>
                  <th className="num money">{money(quote)}</th>
                </tr>
              </tfoot>
            </table>
          ) : null}

          <div className="intake__row">
            <div className="field">
              <label className="field__label" htmlFor="intake-promised">
                Promised for
              </label>
              <DateInput
                id="intake-promised"

                value={promisedDate}
                onChange={(value) => setPromisedDate(value)}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="intake-technician">
                Technician
              </label>
              <select
                id="intake-technician"
                className="select"
                value={technicianId}
                onChange={(event) => setTechnicianId(event.target.value)}
              >
                <option value="">Not assigned yet</option>
                {(roster.data ?? []).map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="intake-notes">
              Notes
            </label>
            <textarea
              id="intake-notes"
              className="input textarea"
              rows={2}
              maxLength={1000}
              placeholder="Skier type, DIN, anything the technician needs to know"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </Card>

        <Card title="Waiver">
          {customer === null ? (
            <p className="text-secondary">Choose the customer first.</p>
          ) : waiver.isPending ? (
            <Loading label="Checking for a waiver on file" />
          ) : waiver.data?.onFile ? (
            <Callout tone="success">
              A waiver is on file — signed by {waiver.data.onFile.signedName} on{' '}
              {new Date(waiver.data.onFile.signedAt).toLocaleDateString()}, covering work taken in
              until {new Date(`${waiver.data.onFile.validUntil}T12:00:00`).toLocaleDateString()}. No
              signature needed today.
            </Callout>
          ) : (
            <>
              <div className="waiver__text" tabIndex={0} aria-label="Waiver wording">
                {waiver.data?.text}
              </div>
              <div className="field">
                <label className="field__label" htmlFor="intake-signed-name">
                  Printed name of the person signing
                </label>
                <input
                  id="intake-signed-name"
                  className="input"
                  maxLength={120}
                  value={signedName}
                  onChange={(event) => setSignedName(event.target.value)}
                />
              </div>
              <SignaturePad onChange={setSignature} />
            </>
          )}
        </Card>
      </div>
    </Screen>
  );
}

function CustomerChooser({
  customer,
  onChosen,
}: {
  customer: { id: string; name: string } | null;
  onChosen: (customer: { id: string; name: string } | null) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 120);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: ['customers', 'search', debounced],
    queryFn: () => api.customers.search.query({ term: debounced, limit: 6 }),
    enabled: debounced.length > 0,
    staleTime: 10_000,
  });

  if (customer !== null) {
    return (
      <div className="intake__chosen">
        <strong>{customer.name}</strong>
        <Button variant="ghost" onClick={() => onChosen(null)}>
          Change
        </Button>
      </div>
    );
  }

  return (
    <>
      <label className="visually-hidden" htmlFor="intake-customer">
        Find the customer
      </label>
      <input
        id="intake-customer"
        className="search__input"
        autoFocus
        autoComplete="off"
        placeholder="Name, phone or email"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      {results.data !== undefined && results.data.length > 0 ? (
        <ul className="intake__results">
          {results.data.map((found) => {
            const name = [found.firstName, found.lastName].filter(Boolean).join(' ');
            return (
              <li key={found.id}>
                <button
                  type="button"
                  className="intake__result"
                  onClick={() => onChosen({ id: found.id, name })}
                >
                  <span>{name}</span>
                  <span className="text-secondary">{found.phone ?? found.email ?? ''}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {/* Never a dead end (§2.2-11): somebody new is added without leaving. */}
      <button type="button" className="link-button" onClick={() => setAdding(true)}>
        {debounced === '' ? 'New customer? Add them' : `Not listed? Add “${debounced}”`}
      </button>
      {adding ? (
        <NewCustomerDialog
          term={debounced}
          onClose={() => setAdding(false)}
          onCreated={async (id) => {
            const created = await api.customers.get.query({ customerId: id });
            setAdding(false);
            onChosen({
              id,
              name: [created.customer.firstName, created.customer.lastName]
                .filter(Boolean)
                .join(' '),
            });
          }}
        />
      ) : null}
    </>
  );
}

// --- the ticket -----------------------------------------------------------------

function TicketScreen({
  ticketId,
  fresh,
  onBack,
  onRungUp,
}: {
  ticketId: string;
  fresh: boolean;
  onBack: () => void;
  onRungUp: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const key = ['repairs', 'get', ticketId];
  const detail = useQuery({
    queryKey: key,
    queryFn: () => api.repairs.get.query({ ticketId }),
    // Same reason as the board: a ticket is opened to act on its current state.
    refetchOnMount: 'always',
  });
  const accept = (next: RepairDetail) => queryClient.setQueryData(key, next);
  const printer = useRepairPrinter();
  const [canceling, setCanceling] = useState(false);

  const stations = useQuery({
    queryKey: ['register', 'stations'],
    queryFn: () => api.register.stations.query(),
  });
  // The register this till rings up on — the same one the Register screen uses.
  const registerId = stations.data?.till.registerId ?? null;

  const move = useMutation({
    mutationFn: (to: 'intake' | 'in_progress' | 'ready') =>
      api.repairs.setStatus.mutate({ ticketId, to }),
    onSuccess: (next, to) => {
      recordBreadcrumb('repairs', `${next.ticket.number} → ${to}`);
      accept(next);
    },
  });
  const update = useMutation({
    mutationFn: (patch: {
      technicianId?: string | null;
      promisedDate?: string | null;
      notes?: string | null;
    }) => api.repairs.update.mutate({ ticketId, ...patch }),
    onSuccess: (next, patch) => {
      const what =
        patch.notes !== undefined
          ? 'notes'
          : patch.technicianId !== undefined
            ? 'technician'
            : 'promised date';
      notify({ title: `Saved — ${next.ticket.number}, ${what}` });
      accept(next);
    },
  });
  const ringUp = useMutation({
    mutationFn: () => api.repairs.ringUp.mutate({ ticketId, registerId: registerId! }),
    onSuccess: async () => {
      recordBreadcrumb('repairs', `rang up ${detail.data?.ticket.number ?? ''}`);
      notify({
        title:
          detail.data === undefined
            ? 'Repair added to the sale'
            : `${detail.data.ticket.number} added to the sale`,
      });
      await queryClient.invalidateQueries({ queryKey: ['register', 'currentSale', registerId] });
      onRungUp();
    },
  });

  if (detail.isPending) {
    return (
      <Screen title="Repair" width="wide">
        <Loading label="Loading the ticket" />
      </Screen>
    );
  }
  if (detail.isError) {
    return (
      <Screen
        title="Repair"
        width="wide"
        secondaryActions={
          <Button variant="ghost" onClick={onBack}>
            Back to the board
          </Button>
        }
      >
        <Callout tone="danger">{errorMessage(detail.error)}</Callout>
      </Screen>
    );
  }

  const { ticket, customer, lines, waiver } = detail.data;
  const status = ticket.status as Status;
  const open = status === 'intake' || status === 'in_progress' || status === 'ready';
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
  const error = move.error ?? update.error ?? ringUp.error;

  const primary =
    status === 'intake' ? (
      <Button variant="primary" busy={move.isPending} onClick={() => move.mutate('in_progress')}>
        Start work
      </Button>
    ) : status === 'in_progress' ? (
      <Button variant="primary" busy={move.isPending} onClick={() => move.mutate('ready')}>
        Mark ready for pickup
      </Button>
    ) : status === 'ready' ? (
      <Button
        variant="primary"
        busy={ringUp.isPending}
        disabledReason={registerId === null ? 'Finding this device’s register…' : undefined}
        onClick={() => ringUp.mutate()}
      >
        Ring up at the register
      </Button>
    ) : undefined;

  return (
    <Screen
      title={
        <>
          <Mono>{ticket.number}</Mono> · {name}
        </>
      }
      subtitle={ticket.equipment}
      width="wide"
      primaryAction={primary}
      secondaryActions={
        <>
          <Button variant="ghost" onClick={onBack}>
            Back to the board
          </Button>
          {status === 'in_progress' ? (
            <Button variant="secondary" onClick={() => move.mutate('intake')}>
              Back to taken in
            </Button>
          ) : null}
          {status === 'ready' ? (
            <Button variant="secondary" onClick={() => move.mutate('in_progress')}>
              Back to in progress
            </Button>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => printer.print({ kind: 'ticket', detail: detail.data })}
          >
            Print ticket
          </Button>
          <Button
            variant="secondary"
            disabledReason={
              waiver === null ? 'No signed waiver is on file for this ticket' : undefined
            }
            onClick={() => printer.print({ kind: 'waiver', detail: detail.data })}
          >
            Print waiver
          </Button>
        </>
      }
      destructiveAction={
        open ? (
          <Button variant="danger" onClick={() => setCanceling(true)}>
            Cancel ticket
          </Button>
        ) : undefined
      }
    >
      {printer.frame}
      {fresh ? (
        <Callout tone="success" title={`${ticket.number} taken in`}>
          Print the ticket for the customer to bring back.
        </Callout>
      ) : null}
      {error !== null ? <Callout tone="danger">{errorMessage(error)}</Callout> : null}
      {status === 'picked_up' ? (
        <Callout tone="success">
          Picked up
          {ticket.pickedUpAt ? ` on ${new Date(ticket.pickedUpAt).toLocaleDateString()}` : ''}
          {ticket.sale?.number ? ` — paid on sale ${ticket.sale.number}` : ''}.
        </Callout>
      ) : null}
      {status === 'cancelled' ? (
        <Callout tone="neutral">Canceled: {ticket.cancelReason}</Callout>
      ) : null}

      <div className="ticket">
        <Card title="The work">
          <p>
            <Badge tone={STATUS_TONE[status]}>{STATUS_WORDS[status]}</Badge>
          </p>
          <table className="table">
            <tbody>
              {lines.map((line) => (
                <tr key={line.id}>
                  <td>
                    {line.quantity > 1 ? `${line.quantity} × ` : ''}
                    {line.description}
                  </td>
                  <td className="num money">{money(line.quantity * line.unitPriceCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Quote, before tax</th>
                <th className="num money">{money(detail.data.quoteCents)}</th>
              </tr>
            </tfoot>
          </table>

          <div className="intake__row">
            <div className="field">
              <label className="field__label" htmlFor="ticket-promised">
                Promised for
              </label>
              <DateInput
                id="ticket-promised"

                disabled={!open}
                value={ticket.promisedDate ?? ''}
                onChange={(value) => update.mutate({ promisedDate: value || null })}
              />
            </div>
            <TechnicianField
              value={ticket.technicianId}
              disabled={!open}
              onChange={(technicianId) => update.mutate({ technicianId })}
            />
          </div>
          <NotesField
            key={ticket.notes ?? ''}
            value={ticket.notes}
            disabled={!open}
            onSave={(notes) => update.mutate({ notes })}
          />
          <p className="text-secondary">
            Taken in by {ticket.intakeByName ?? 'the shop'} on{' '}
            {new Date(ticket.createdAt).toLocaleString()}.
          </p>
        </Card>

        <Card title="Customer and waiver">
          <p>
            <strong>{name}</strong>
            <br />
            <span className="text-secondary">
              {[customer.phone, customer.email].filter(Boolean).join(' · ') || 'No phone or email'}
            </span>
          </p>
          {waiver === null ? (
            <Callout tone="warning">No signed waiver is on file for this ticket.</Callout>
          ) : (
            <>
              {waiver.signaturePath === null ? null : (
                <SignatureImage
                  path={waiver.signaturePath}
                  width={waiver.signatureWidth}
                  height={waiver.signatureHeight}
                  label={`Signature of ${waiver.signedName}`}
                />
              )}
              <p className="text-secondary">
                Signed by {waiver.signedName} on {new Date(waiver.signedAt).toLocaleString()}.
              </p>
            </>
          )}
        </Card>
      </div>

      {canceling ? (
        <CancelTicketDialog
          number={ticket.number}
          onClose={() => setCanceling(false)}
          onCanceled={(next) => {
            accept(next);
            setCanceling(false);
          }}
          ticketId={ticketId}
        />
      ) : null}
    </Screen>
  );
}

function TechnicianField({
  value,
  disabled,
  onChange,
}: {
  value: string | null;
  disabled: boolean;
  onChange: (technicianId: string | null) => void;
}) {
  const roster = useQuery({
    queryKey: ['auth', 'pinPadRoster'],
    queryFn: () => api.auth.pinPadRoster.query(),
    staleTime: 60_000,
  });
  return (
    <div className="field">
      <label className="field__label" htmlFor="ticket-technician">
        Technician
      </label>
      <select
        id="ticket-technician"
        className="select"
        disabled={disabled}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      >
        <option value="">Not assigned</option>
        {(roster.data ?? []).map((person) => (
          <option key={person.id} value={person.id}>
            {person.displayName}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Saved when it loses focus, never per keystroke (DESIGN-DNA Part 6). */
function NotesField({
  value,
  disabled,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (notes: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  return (
    <div className="field">
      <label className="field__label" htmlFor="ticket-notes">
        Notes
      </label>
      <textarea
        id="ticket-notes"
        className="input textarea"
        rows={2}
        maxLength={1000}
        disabled={disabled}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (draft.trim() !== (value ?? '')) onSave(draft.trim() === '' ? null : draft.trim());
        }}
      />
    </div>
  );
}

function CancelTicketDialog({
  ticketId,
  number,
  onClose,
  onCanceled,
}: {
  ticketId: string;
  number: string;
  onClose: () => void;
  onCanceled: (next: RepairDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: () => api.repairs.cancel.mutate({ ticketId, reason: reason.trim() }),
    onSuccess: (next) => {
      recordBreadcrumb('repairs', `canceled ${number}`);
      onCanceled(next);
    },
  });
  return (
    <Modal
      open
      tone="danger"
      title={`Cancel ${number}?`}
      description="The ticket stays on record with the reason. Nothing is charged."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Keep it
        </Button>
      }
      destructiveAction={
        <Button
          variant="danger"
          busy={cancel.isPending}
          disabledReason={
            reason.trim() === '' ? 'Say why — the reason is the only record' : undefined
          }
          onClick={() => cancel.mutate()}
        >
          Cancel the ticket
        </Button>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="cancel-reason">
          Why is it being canceled?
        </label>
        <input
          id="cancel-reason"
          className="input"
          autoFocus
          maxLength={200}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      {cancel.isError ? <Callout tone="danger">{errorMessage(cancel.error)}</Callout> : null}
    </Modal>
  );
}
